import * as React from 'react';
import { ArrowRight, FileText, RefreshCw, X } from 'lucide-react';
import apiService from '../../services/api';
import { localizeSkillMeta } from '../../features/agent/skills/builtInSkills';
import { getBankOfChangesCopy } from '../../features/agent/skills/bankOfChanges/bankOfChangesCopy';
import type { BankOfChangesCopy } from '../../features/agent/skills/bankOfChanges/bankOfChangesCopy';
import {
  activitySummary,
  clusterDetailLines,
  clusterTitle,
  makeInitialBankSession,
  fileNotice,
  reduceBankSession,
  WORD_ATTACHMENT_ACCEPT,
} from '../../features/agent/skills/bankOfChanges/bankOfChangesFlow';
import {
  draftIsOpen,
  draftPayload,
  draftSignature,
  filesSignature,
  forgetLocalConversations,
  sessionFromServer,
  worthSaving,
} from '../../features/agent/skills/bankOfChanges/bankConversationSync';
import type { BankAction } from '../../features/agent/skills/bankOfChanges/bankOfChangesFlow';
import { BankOfChangesAuthError, BankOfChangesServerError } from '../../features/agent/skills/bankOfChanges/types';
import type {
  BankActivity,
  BankConversationMeta,
  BankOutputFile,
  BankSessionState,
  BankUpload,
} from '../../features/agent/skills/bankOfChanges/types';
import type { AgentSkillMeta } from '../../features/agent/skills/types';
import { fileToBase64Payload } from '../../utils/fileAttachments';
import { downloadBase64File, paneCanDownload } from '../../utils/generatedWordFile';
import { openExternalUrl } from '../../utils/openExternalUrl';
import { CostLimitExceededError } from '../../utils/usageLimitError';
import { logError } from '../../utils/logging';
import { ChatComposer } from './ChatComposer';
import { RunActivity } from './conversation/RunActivity';
import { ConversationHeaderActions, SavedConversations } from './conversation/SavedConversations';
import './conversation/conversationLook.css';
import './SkillRunner.css';
import './BankOfChangesRunner.css';

/** How long a file's card says that its download started. */
const DOWNLOAD_STATUS_MS = 5000;
/** How long the conversation waits after its last change before it is saved on the server. */
const SAVE_DELAY_MS = 600;
/** A run that was just stopped holds the conversation for a moment; the save waits and tries again. */
const SAVE_RETRY_MS = 2000;
const SAVE_RETRIES = 5;
/** How often the pane asks the server how far a run got. */
const FOLLOW_INTERVAL_MS = 1500;
/** How often the list of conversations is read again while one of them is being worked on. */
const LIST_REFRESH_MS = 4000;

async function encodeFile(file: File): Promise<string> {
  return (await fileToBase64Payload(file)).content_base64;
}

/** Every picked or dropped file reaches the runner, so it can say why one was
 * refused (not a Word file, a duplicate, …) instead of dropping it silently. */
function keepAllAttachments(files: FileList | File[]): File[] {
  return Array.from(files);
}

/** A server-written reason is shown as is; anything else (network, timeout,
 * a broken stream) gets the runner's own Hebrew line. */
function describeFailure(error: unknown, fallback: string, copy: BankOfChangesCopy): string {
  if (error instanceof BankOfChangesServerError) return error.message;
  if (error instanceof CostLimitExceededError) return copy.errorUsageLimit;
  if (error instanceof BankOfChangesAuthError) return copy.errorSignedOut;
  return fallback;
}

/** The progress of one run (a build or a revision): the shared block, in the workflow's words. */
const ActivityBlock: React.FC<{ activity: BankActivity; copy: BankOfChangesCopy }> = ({ activity, copy }) => (
  <RunActivity
    state={activity.state}
    clusters={activity.clusters}
    summary={activitySummary(activity, copy)}
    toggleTitle={copy.activityToggle}
    titleOf={(cluster) => clusterTitle(cluster, copy)}
    linesOf={clusterDetailLines}
  />
);

interface BankConversationProps {
  /** The session this conversation starts from: a new one, or a saved one read back from the server. */
  initial: BankSessionState;
  copy: BankOfChangesCopy;
  /** Called with every state of the conversation, so that the frame knows which conversation is open. */
  onChange: (session: BankSessionState) => void;
  /** The lawyer asked for a new conversation; this one stays on the server. */
  onStartOver: () => void;
}

/**
 * One conversation of the `create-bank-of-changes` workflow: upload the existing
 * bank, upload the signed change appendices, review what the server found in
 * the bank, confirm, watch the build, take the generated Word files, then
 * answer the decisions the agent puts to the lawyer (or ask for any
 * correction) and take a new version. Every version's files stay on the turn
 * that handed them over, so all of them remain downloadable from the history.
 * The box to write in stays in place while the agent works, and its send
 * button becomes a stop button. The conversation itself is the pure state
 * machine in bankOfChangesFlow.ts; this component renders it, makes the server
 * calls, and keeps the conversation on the server (never in the pane) for as
 * long as the pane leads it — see bankConversationSync.ts.
 *
 * A run (the look, the build, an answer) goes on in the server. The pane asks
 * for it, then follows it by asking how far it got, and reads the conversation
 * again when it is over. Leaving the workflow, or closing Word, does not end a
 * run: whoever opens the conversation next — here or in the dashboard — finds
 * it running or finished. Only the stop button ends it.
 */
const BankConversation: React.FC<BankConversationProps> = ({ initial, copy, onChange, onStartOver }) => {
  const [session, dispatch] = React.useReducer(
    (state: BankSessionState, action: BankAction) => reduceBankSession(state, action, copy),
    initial,
  );
  const reportChange = React.useRef(onChange);
  reportChange.current = onChange;
  React.useEffect(() => {
    reportChange.current(session);
  }, [session]);
  const [input, setInput] = React.useState('');
  /** The file a download is being prepared for — a second click would start a second one. */
  const [preparingDownload, setPreparingDownload] = React.useState<BankOutputFile['key'] | null>(null);
  /** What the card of a file says for a few seconds after its download started. A started
   * download adds nothing to the conversation; only a failed one is said in it. */
  const [downloadStatus, setDownloadStatus] = React.useState<{ key: BankOutputFile['key']; text: string } | null>(null);
  const statusTimer = React.useRef<number | null>(null);
  React.useEffect(
    () => () => {
      if (statusTimer.current !== null) window.clearTimeout(statusTimer.current);
    },
    [],
  );
  const showDownloadStatus = (key: BankOutputFile['key'], text: string) => {
    if (statusTimer.current !== null) window.clearTimeout(statusTimer.current);
    setDownloadStatus({ key, text });
    statusTimer.current = window.setTimeout(() => setDownloadStatus(null), DOWNLOAD_STATUS_MS);
  };
  const scrollRef = React.useRef<HTMLDivElement>(null);
  /** The conversation as of the latest render — the server calls below start
   * on a step change and must send what that step was entered with. */
  const sessionRef = React.useRef(session);
  sessionRef.current = session;

  React.useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [session.turns, session.step, session.activity]);

  // --- Keeping the conversation on the server while the pane leads it ----------------------

  /** The conversation's id on the server, known from the first saved draft (before the next render says so). */
  const conversationId = React.useRef<string | null>(initial.conversationId);
  /** The files the server already keeps, by the picked file they came from: each is uploaded once. */
  const uploaded = React.useRef(new Map<BankUpload, string>());
  /** What the last saved draft held, and the files of a draft the server refused. */
  const savedDraft = React.useRef<string | null>(initial.conversationId ? draftSignature(initial) : null);
  const refusedFiles = React.useRef<string | null>(null);
  const saving = React.useRef<Promise<unknown>>(Promise.resolve());
  const retryTimer = React.useRef<number | null>(null);
  const alive = React.useRef(true);

  /** Sends the conversation as it stands, one draft at a time. Resolves with the conversation's id. */
  const saveDraft = React.useCallback((): Promise<string> => {
    const next = saving.current
      .catch(() => undefined)
      .then(async () => {
        const state = sessionRef.current;
        const signature = draftSignature(state);
        if (conversationId.current && savedDraft.current === signature) return conversationId.current;
        const sent = { bank: state.bank, appendices: state.appendices };
        const payload = await draftPayload(state, uploaded.current, encodeFile);
        if (conversationId.current) payload.conversation_id = conversationId.current;
        const conversation = await apiService.saveBankOfChangesDraft(payload);
        conversationId.current = conversation.id;
        if (sent.bank && conversation.bank) uploaded.current.set(sent.bank, conversation.bank.id);
        sent.appendices.forEach((file, index) => {
          const kept = conversation.appendices[index];
          if (kept) uploaded.current.set(file, kept.id);
        });
        savedDraft.current = signature;
        refusedFiles.current = null;
        if (alive.current) dispatch({ type: 'draft_saved', conversation, sent });
        return conversation.id;
      });
    saving.current = next;
    return next;
  }, []);

  /** Saves in the background. A reason the server wrote for the lawyer is said in the conversation, once. */
  const saveInBackground = React.useCallback(
    (attempt: number) => {
      saveDraft().catch((e) => {
        if (!alive.current) return;
        if (e instanceof BankOfChangesServerError && e.status === 409 && attempt < SAVE_RETRIES) {
          retryTimer.current = window.setTimeout(() => saveInBackground(attempt + 1), SAVE_RETRY_MS);
          return;
        }
        logError('[BankOfChangesRunner] saving the conversation failed', e);
        if (e instanceof BankOfChangesServerError && e.status !== 409) {
          refusedFiles.current = filesSignature(sessionRef.current);
          dispatch({ type: 'notice', content: `${copy.draftNotSaved} ${e.message}` });
        }
      });
    },
    [copy, saveDraft],
  );

  React.useEffect(() => {
    if (!worthSaving(session) || !draftIsOpen(session)) return undefined;
    if (conversationId.current && savedDraft.current === draftSignature(session)) return undefined;
    if (refusedFiles.current === filesSignature(session)) return undefined;
    const timer = window.setTimeout(() => saveInBackground(0), SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [session, saveInBackground]);

  // Leaving the conversation, or the pane going away, sends what the server has not got yet.
  React.useEffect(() => {
    // Set here and not only at first render: in development React runs every effect twice
    // (mount, unmount, mount), and the conversation must still count as open after the second mount.
    alive.current = true;
    const flush = () => {
      const state = sessionRef.current;
      if (!worthSaving(state) || !draftIsOpen(state)) return;
      if (refusedFiles.current === filesSignature(state)) return;
      saveDraft().catch((e) => logError('[BankOfChangesRunner] saving the conversation on leaving failed', e));
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
      flush();
      alive.current = false;
    };
  }, [saveDraft]);

  // Starting a run: once each time a running step is entered by the lawyer's own action. The server
  // answers at once and carries the run on itself, so nothing here is tied to this component's life.
  React.useEffect(() => {
    const step = session.step;
    if (session.following || (step !== 'analyzing' && step !== 'building' && step !== 'revising')) return undefined;
    const kind = step === 'analyzing' ? 'analyze' : step === 'building' ? 'build' : 'revise';
    const failed = (message: string): BankAction =>
      kind === 'analyze'
        ? { type: 'analyze_failed', message }
        : kind === 'build'
          ? { type: 'build_failed', message }
          : { type: 'revise_failed', message };
    const fallback =
      kind === 'analyze' ? copy.errorAnalyzeGeneric : kind === 'build' ? copy.errorBuildGeneric : copy.errorReviseGeneric;
    void (async () => {
      try {
        // Before the first version the server works on the files of the last draft: the draft goes first.
        const id = kind === 'revise' ? sessionRef.current.conversationId : await saveDraft();
        if (!id) throw new Error('The conversation is not on the server');
        const conversation = await apiService.startBankOfChangesRun(id, kind, sessionRef.current.pendingRequest ?? undefined);
        if (alive.current) dispatch({ type: 'run_started', conversation });
      } catch (e) {
        logError(`[BankOfChangesRunner] starting the ${kind} failed`, e);
        if (alive.current) dispatch(failed(describeFailure(e, fallback, copy)));
      }
    })();
    return undefined;
  }, [session.step, session.following, copy, saveDraft]);

  // Following a run that goes on in the server: ask how far it got, and when it is over read the
  // conversation again — what the run came to is in it, written by the server. Stopping to follow
  // (the pane closes, the lawyer leaves the workflow) does not touch the run.
  React.useEffect(() => {
    const id = session.conversationId;
    if (!session.following || !id) return undefined;
    let following = true;
    let timer: number | null = null;
    const ask = async () => {
      try {
        const conversation = await apiService.getBankOfChangesRun(id);
        if (!following) return;
        if (conversation.run) {
          dispatch({ type: 'run_progress', run: conversation.run });
        } else {
          const found = await apiService.getBankOfChangesConversation(id);
          if (!following) return;
          const loaded = sessionFromServer(found.conversation, found.messages, copy);
          // The server has everything the conversation shows now; nothing of it needs saving again.
          savedDraft.current = draftSignature(loaded);
          dispatch({ type: 'conversation_loaded', session: loaded });
          // Another run may have started between the two answers (from the dashboard): it is followed too.
          if (!loaded.following) return;
        }
      } catch (e) {
        // The connection may be down for a moment; the run goes on in the server either way.
        logError('[BankOfChangesRunner] asking how far the run got failed', e);
      }
      if (following) timer = window.setTimeout(() => void ask(), FOLLOW_INTERVAL_MS);
    };
    timer = window.setTimeout(() => void ask(), FOLLOW_INTERVAL_MS);
    return () => {
      following = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [session.following, session.conversationId, copy]);

  const handleStop = () => {
    const id = sessionRef.current.conversationId;
    if (!id || !sessionRef.current.following || sessionRef.current.stopping) return;
    dispatch({ type: 'stop' });
    apiService.stopBankOfChangesRun(id).catch((e) => {
      // The server was not asked: the run goes on, and the button is there to press again.
      logError('[BankOfChangesRunner] asking the run to stop failed', e);
      if (alive.current) dispatch({ type: 'stop_failed' });
    });
  };

  const running = session.step === 'building' || session.step === 'revising';
  const busy = session.step === 'analyzing' || running;

  const handleDownload = async (file: BankOutputFile) => {
    const id = sessionRef.current.conversationId;
    if (preparingDownload || !id) return;
    setPreparingDownload(file.key);
    try {
      if (paneCanDownload()) {
        const base64 = await apiService.downloadBankOfChangesFile(id, file.id);
        const outcome = downloadBase64File({ base64, filename: file.filename });
        if (!outcome.ok) throw new Error(outcome.error);
        showDownloadStatus(file.key, copy.downloadStarted);
      } else {
        // Desktop Word: the pane cannot save a file. The server hands its own copy over behind a
        // one-time link, and the system browser downloads it.
        const link = await apiService.createBankOfChangesDownloadLink(id, file.id);
        openExternalUrl(link.url);
        showDownloadStatus(file.key, copy.downloadOpenedInBrowser);
      }
    } catch (e) {
      logError('[BankOfChangesRunner] download failed', e);
      dispatch({ type: 'notice', content: fileNotice(file, copy.downloadFailed) });
    } finally {
      setPreparingDownload(null);
    }
  };

  const awaitingFiles = session.step === 'awaiting_appendices' || session.step === 'awaiting_confirmation';
  // Once the files are ready the lawyer answers the agent's follow-up in the same box.
  const awaitingAnswer = session.step === 'done' && session.answerable;
  // The box stays in place while the agent works: the lawyer sees the conversation as it is, can
  // write the next message meanwhile, and can stop the work. It is absent only when the conversation
  // ended with files and nothing to answer to.
  const showComposer = busy || session.step === 'awaiting_bank' || awaitingFiles || awaitingAnswer;
  const composerPlaceholder = session.stopping
    ? copy.placeholderStopping
    : busy
    ? copy.placeholderRunning
    : awaitingAnswer
    ? copy.placeholderFollowup
    : session.step === 'awaiting_bank'
      ? copy.placeholderBank
      : session.step === 'awaiting_confirmation'
        ? copy.placeholderConfirmation
        : copy.placeholderAppendices;
  const composerFollowUp = session.turns.some((turn) => turn.role === 'user');
  const uploadedCount = (session.bank ? 1 : 0) + session.appendices.length;

  return (
    <>
      <div className="skill-runner-thread">
        {session.turns.map((turn) => (
          <div key={turn.id} className={`skill-runner-turn skill-runner-turn--${turn.role}`} dir="auto">
            {turn.activity ? (
              // A finished run stays in the transcript as one line that opens to its stages.
              <ActivityBlock activity={turn.activity} copy={copy} />
            ) : turn.files && turn.files.length > 0 ? (
              // The files stay on the turn that handed them over: an earlier version is
              // downloaded from here after any number of later ones.
              <div className="skill-runner-turn-body">
                <div className="skill-runner-turn-content">{turn.content}</div>
                <div className="skill-runner-turn-files">
                  {turn.files.map((file) => (
                    <div key={file.key} className="boc-file-card" title={file.filename}>
                      <div className="boc-file-icon" aria-hidden>
                        <FileText size={18} />
                      </div>
                      <div className="boc-file-meta">
                        <div className="boc-file-title">{file.label}</div>
                        <div className="boc-file-detail" aria-live="polite">
                          {downloadStatus?.key === file.key ? downloadStatus.text : file.detail}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="skill-runner-option-btn boc-file-download"
                        data-file-key={file.key}
                        aria-label={file.downloadLabel}
                        disabled={preparingDownload !== null}
                        onClick={() => void handleDownload(file)}
                      >
                        {preparingDownload === file.key ? copy.downloadPreparing : copy.download}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="skill-runner-turn-content">{turn.content}</div>
            )}
          </div>
        ))}

        {running && session.activity && (
          <div className="skill-runner-turn skill-runner-turn--assistant" dir="auto" aria-live="polite">
            <ActivityBlock activity={session.activity} copy={copy} />
          </div>
        )}

        {awaitingFiles && uploadedCount > 0 && (
          <details className="skill-runner-files">
            <summary className="skill-runner-files-summary">{copy.filesSummary(uploadedCount)}</summary>
            <ul className="skill-runner-files-list">
              {session.bank && (
                <li className="skill-runner-file-row">
                  <span className="skill-runner-file-name" dir="auto" title={session.bank.name}>
                    {session.bank.name}
                  </span>
                  <span className="skill-runner-file-tag">{copy.bankTag}</span>
                  <button
                    type="button"
                    className="skill-runner-file-action"
                    onClick={() => dispatch({ type: 'replace_bank' })}
                  >
                    {copy.replaceBank}
                  </button>
                </li>
              )}
              {session.appendices.map((file, index) => (
                <li key={`${file.name}-${file.size}`} className="skill-runner-file-row">
                  <span className="skill-runner-file-name" dir="auto" title={file.name}>
                    {file.name}
                  </span>
                  <button
                    type="button"
                    className="skill-runner-file-action"
                    title={copy.removeFile(file.name)}
                    aria-label={copy.removeFile(file.name)}
                    onClick={() => dispatch({ type: 'remove_appendix', index })}
                  >
                    <X size={12} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}

        {session.step === 'awaiting_appendices' && session.appendices.length > 0 && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              onClick={() => dispatch({ type: 'finish_upload' })}
            >
              {copy.doneUploading}
            </button>
          </div>
        )}

        {session.step === 'awaiting_confirmation' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={session.appendices.length === 0}
              onClick={() => dispatch({ type: 'start_build' })}
            >
              {copy.start}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              onClick={() => dispatch({ type: 'request_more_appendices' })}
            >
              {copy.addMoreAppendices}
            </button>
          </div>
        )}

        {session.step === 'done' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              onClick={() => {
                setInput('');
                onStartOver();
              }}
            >
              {copy.startOver}
            </button>
          </div>
        )}

        {/* A build or a revision shows its own progress above; only the look has nothing to show but that it works. */}
        {busy && !running && (
          <div className="skill-runner-busy">
            <RefreshCw size={12} className="spinning" aria-hidden />
            <span>{session.stopping ? copy.stopping : copy.working}</span>
          </div>
        )}

        <div ref={scrollRef} />
      </div>

      {showComposer && (
        <div className="skill-runner-composer skill-runner-composer--files">
          <ChatComposer
            value={input}
            onChange={setInput}
            onSubmit={({ text, attachments }) => {
              dispatch({ type: 'submit', text, files: attachments });
              setInput('');
            }}
            placeholder={composerPlaceholder}
            layout="sticky"
            followUp={composerFollowUp}
            submitTitle={copy.submitTitle}
            showAgentModeSelector={false}
            attachmentAccept={WORD_ATTACHMENT_ACCEPT}
            filterAttachments={keepAllAttachments}
            showImageUpload={false}
            onStop={busy && session.following && !session.stopping ? handleStop : undefined}
            stopTitle={copy.stop}
          />
        </div>
      )}
    </>
  );
};

/** The account's conversations of the workflow: the shared list, in the workflow's words. */
const SessionsPanel: React.FC<{
  copy: BankOfChangesCopy;
  sessions: BankConversationMeta[] | null;
  currentId: string | null;
  failedId: string | null;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
}> = ({ copy, sessions, ...rest }) => {
  const now = Date.now();
  return (
    <SavedConversations
      labels={{
        title: copy.savedSessions,
        close: copy.closePanel,
        newConversation: copy.newConversation,
        loading: copy.sessionsLoading,
        empty: copy.sessionsEmpty,
        current: copy.currentSession,
        confirmDelete: copy.confirmDelete,
        confirmDeleteYes: copy.confirmDeleteYes,
        confirmDeleteNo: copy.confirmDeleteNo,
        open: copy.openSession,
        remove: copy.deleteSession,
        openFailed: copy.sessionOpenFailed,
        note: copy.sessionsNote,
      }}
      rows={
        sessions &&
        sessions.map((item) => ({
          id: item.id,
          title: item.title || copy.untitledSession,
          running: item.running,
          detail: item.running
            ? copy.sessionRunning(item.runningKind)
            : `${copy.sessionStatus(item.step, item.version, item.appendixCount)} · ${copy.sessionWhen(item.updatedAt, now)}`,
        }))
      }
      {...rest}
    />
  );
};

interface BankOfChangesRunnerProps {
  skill: AgentSkillMeta;
  onBack: () => void;
}

/** `key` tells one opened conversation from the next; a conversation's own id is the server's, in its session. */
type OpenConversation = { key: string; initial: BankSessionState };

let openedConversations = 0;

/**
 * The frame of the workflow's chat: which conversation is open, and the saved
 * ones. A conversation is kept on the server from the moment a file is put
 * into it, with every file it holds; the pane keeps nothing of it. So leaving
 * the workflow, or the pane reloading, loses nothing: the lawyer comes back to
 * it from the list behind the history button, or from the dashboard's chat,
 * with every version it produced still there to download. A run that was going
 * on when the conversation was left is stopped, and the conversation says so.
 */
export const BankOfChangesRunner: React.FC<BankOfChangesRunnerProps> = ({ skill, onBack }) => {
  const displaySkill = React.useMemo(() => localizeSkillMeta(skill), [skill]);
  const copy = React.useMemo(() => getBankOfChangesCopy(), []);
  const fresh = React.useCallback((): OpenConversation => {
    openedConversations += 1;
    return { key: `conversation-${openedConversations}`, initial: makeInitialBankSession(copy) };
  }, [copy]);
  const [open, setOpen] = React.useState<OpenConversation>(fresh);
  /** The open conversation's id on the server, once it has one. */
  const [currentId, setCurrentId] = React.useState<string | null>(null);
  const [sessions, setSessions] = React.useState<BankConversationMeta[] | null>(null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [failedId, setFailedId] = React.useState<string | null>(null);
  const mounted = React.useRef(true);

  const refresh = React.useCallback(() => {
    apiService
      .listBankOfChangesConversations()
      .then((list) => {
        if (mounted.current) setSessions(list);
      })
      .catch((e) => logError('[BankOfChangesRunner] listing saved conversations failed', e));
  }, []);

  const shown = React.useRef<{ id: string | null; step: string; version: number; following: boolean }>({
    id: null,
    step: '',
    version: 0,
    following: false,
  });
  const handleChange = React.useCallback(
    (session: BankSessionState) => {
      const before = shown.current;
      shown.current = { id: session.conversationId, step: session.step, version: session.version, following: session.following };
      if (session.conversationId !== before.id) setCurrentId(session.conversationId);
      // The list says where each conversation stands and whether it is being worked on, so it is read
      // again when this one moved on, and when the server took its run or ended it.
      const moved =
        session.conversationId !== before.id ||
        session.step !== before.step ||
        session.version !== before.version ||
        session.following !== before.following;
      if (session.conversationId && moved) refresh();
    },
    [refresh],
  );

  React.useEffect(() => {
    // Set here and not only at first render: in development React runs every effect twice
    // (mount, unmount, mount), and the list must still arrive after the second mount.
    mounted.current = true;
    // Earlier versions kept conversations on this computer. They are deleted from it.
    forgetLocalConversations();
    refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const switchTo = (next: OpenConversation) => {
    shown.current = {
      id: next.initial.conversationId,
      step: next.initial.step,
      version: next.initial.version,
      following: next.initial.following,
    };
    setCurrentId(next.initial.conversationId);
    setFailedId(null);
    setPanelOpen(false);
    // The conversation that was open sends its last draft as it closes.
    setOpen(next);
  };

  const handleOpen = (id: string) => {
    if (id === currentId) {
      setPanelOpen(false);
      return;
    }
    apiService
      .getBankOfChangesConversation(id)
      .then((found) => {
        if (!mounted.current) return;
        openedConversations += 1;
        switchTo({
          key: `conversation-${openedConversations}`,
          initial: sessionFromServer(found.conversation, found.messages, copy),
        });
      })
      .catch((e) => {
        logError('[BankOfChangesRunner] opening a saved conversation failed', e);
        if (mounted.current) setFailedId(id);
      });
  };

  const handleDelete = (id: string) => {
    apiService
      .deleteBankOfChangesConversation(id)
      .then(() => {
        if (!mounted.current) return;
        // Deleting the open conversation leaves a new, empty one in its place.
        if (id === currentId) switchTo(fresh());
        refresh();
      })
      .catch((e) => logError('[BankOfChangesRunner] deleting a saved conversation failed', e));
  };

  const savedCount = sessions?.length ?? 0;
  const runningCount = (sessions ?? []).filter((item) => item.running).length;

  // While a conversation is being worked on, and while the list is open, the list is read again every
  // few seconds: a row (and the mark on the history button) says that a run is going on for as long as
  // it is true and no longer, also for a run that was started elsewhere (the dashboard).
  React.useEffect(() => {
    if (runningCount === 0 && !panelOpen) return undefined;
    const timer = window.setInterval(refresh, LIST_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [runningCount, panelOpen, refresh]);

  return (
    <div className="skill-runner boc-chat" dir="rtl">
      <div className="skill-runner-header">
        {/* Stays enabled while a server call runs: leaving stops it, and the conversation is kept. */}
        <button type="button" className="skill-runner-back" onClick={onBack}>
          <ArrowRight size={14} aria-hidden />
          {copy.backToWorkflows}
        </button>
        <span className="skill-runner-title">{displaySkill.name}</span>
        <ConversationHeaderActions
          savedLabel={copy.savedSessions}
          savedTitle={
            runningCount > 0
              ? copy.sessionsRunningCount(runningCount)
              : savedCount > 0
                ? copy.savedSessionsCount(savedCount)
                : copy.savedSessions
          }
          newLabel={copy.newConversation}
          savedCount={savedCount}
          runningCount={runningCount}
          panelOpen={panelOpen}
          onTogglePanel={() => {
            if (!panelOpen) refresh();
            setFailedId(null);
            setPanelOpen((value) => !value);
          }}
          onNew={() => switchTo(fresh())}
        />
      </div>

      <div className="boc-body">
        <BankConversation
          key={open.key}
          initial={open.initial}
          copy={copy}
          onChange={handleChange}
          onStartOver={() => switchTo(fresh())}
        />
        {panelOpen && (
          <SessionsPanel
            copy={copy}
            sessions={sessions}
            currentId={currentId}
            failedId={failedId}
            onOpen={handleOpen}
            onDelete={handleDelete}
            onNew={() => switchTo(fresh())}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>
    </div>
  );
};
